import React from "react";
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { apiPostRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { useTranslation } from "react-i18next";
import { useDirection } from "../../../../Context/DirectionContext";
import { soonBadge } from "../../styles/styles";
import { logoutHandler } from "../../../../Utils/auth";
import { createOnboardingAppUrl } from "../../../../Helpers/Actions";
import { MEMBERS_PLATFORM_URL } from "../../../../Constants/config";
function SettingsContent(props) {
  const {
    modalPosition,
    isBelowMd,
    setIsProfileOpen,
    setProfileDrawerTab,
    setIsModalVisible,
    sidebarData,
    languages = [],
    onLanguageChange,
  } = props;
  const navigate = useNavigate();
  const { t } = useTranslation(["bottom-menu-and-settings"]);
  const { i18n } = useTranslation();

  const { toggleDirection } = useDirection();

  const {
    isSupplier,
    isCommunityManager,
    isMemberInCommunity,
    isModerator,
    last_role_view,
    isMoM,
    momView,
  } = sidebarData;

  const hasCommunityManagementAccess = isCommunityManager || isModerator;

  // Anchored by its bottom edge, not its top: how tall the menu is depends on
  // which items the user's roles unlock, so a fixed top offset pushed the
  // longer variants off the bottom of the screen. The arrow sits a fixed
  // distance from the bottom and points at the Settings item.
  const ARROW_FROM_BOTTOM = 40;
  const SETTINGS_ITEM_HALF_HEIGHT = 22;
  const webStyling = {
    position: "absolute",
    bottom: `${Math.max(
      16,
      window.innerHeight -
        (modalPosition.top - SETTINGS_ITEM_HALF_HEIGHT) -
        ARROW_FROM_BOTTOM
    )}px`,
    left: `280px`,
    bgcolor: "background.paper",
    py: "24px",
    px: "16px",
    height: "auto",
    borderRadius: "12px",
    minWidth: "248px",
    maxWidth: "320px",
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: `${ARROW_FROM_BOTTOM}px`,
      left: "-12px",
      transform: "translateY(50%)",
      width: 0,
      height: 0,
      borderTop: "16px solid transparent",
      borderBottom: "16px solid transparent",
      borderRight: "16px solid white",
    },
  };
  const mobileStyling = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  };

  const switchRole = async () => {
    try {
      const response = await apiPostRequest(endpoints.toggleAdminSupplierRole);
      const currentRole = response?.data?.currentRole;

      if (currentRole === 2) {
        window.location.href = "/my-communities";
      } else {
        window.location.href = "/my-suppliers";
      }
    } catch (error) {
      console.error("Failed to switch roles:", error);
    }
  };

  const memberPlatformItem = (disabled = true) => ({
    label: t("memberPlatform"),
    iconPath: "/icons/sidebar-icons/global.svg",
    disabled,
    onClick: () => {
      window.location.href = MEMBERS_PLATFORM_URL;
    },
  });

  const redirectToOnboarding = (path) => () =>
    (window.location.href = createOnboardingAppUrl({
      path,
      appendSource: true,
    }));

  const getRoleMenuItem = () => {
    let primaryItem = null;
    let isMemberPlatformDisabled = false;

    if (hasCommunityManagementAccess && !isSupplier) {
      // Community Manager / Moderator only
      primaryItem = {
        label: t("becomeSupplier"),
        iconPath: "/icons/sidebar-icons/supplier.svg",
        onClick: redirectToOnboarding("/add_supplier"),
      };
      isMemberPlatformDisabled = false;
    } else if (!hasCommunityManagementAccess && isSupplier) {
      // Supplier only
      primaryItem = {
        label: t("createCommunity"),
        iconPath: "/icons/sidebar-icons/users-plus.svg",
        onClick: redirectToOnboarding("/create_community"),
        disabled: true,
        showSoonBadge: true,
      };
      isMemberPlatformDisabled = !isMemberInCommunity;
    } else if (!hasCommunityManagementAccess && !isSupplier) {
      // Neither role
      primaryItem = {
        label: t("needToBecomeEitherSupplierOrAdmin"),
        iconPath: "/icons/sidebar-icons/supplier.svg",
        onClick: redirectToOnboarding("/platform"),
      };
      isMemberPlatformDisabled = !isMemberInCommunity;
    } else if (hasCommunityManagementAccess && isSupplier) {
      // Both roles
      const isSupplierView = last_role_view === "SUPPLIER";

      primaryItem = isSupplierView
        ? {
            label: t("communityManagement"),
            iconPath: "/icons/sidebar-icons/users-plus.svg",
            onClick: switchRole,
          }
        : {
            label: t("businessManagement"),
            iconPath: "/icons/sidebar-icons/supplier.svg",
            onClick: switchRole,
          };

      isMemberPlatformDisabled = false;
    }

    if (!primaryItem) return [];

    return [primaryItem, memberPlatformItem(isMemberPlatformDisabled)];
  };

  const getLanguageToggleLabel = () => {
    const currentLang = i18n.resolvedLanguage;

    if (currentLang === "en") {
      return "עברית";
    }

    if (currentLang === "he") {
      return "English";
    }

    const otherLang = languages.find(
      (lang) => lang.code.trim() !== currentLang,
    );

    return otherLang ? otherLang.name : "Language";
  };

  const handleLanguageToggle = () => {
    const currentLang = i18n.resolvedLanguage;
    const otherLang = languages.find(
      (lang) => lang.code.trim() !== currentLang,
    );

    if (otherLang) {
      onLanguageChange?.(otherLang.id);
      toggleDirection();
    }
  };

  const settingsMenuItems = [
    {
      label: t("myProfile"),
      iconPath: "/icons/sidebar-icons/profile.svg",
      onClick: () => {
        setIsProfileOpen(true);
        setProfileDrawerTab("profile");
      },
    },
    {
      label: t("myOrders"),
      iconPath: "/icons/sidebar-icons/order.svg",
      onClick: () => {
        setIsProfileOpen(true);
        setProfileDrawerTab("orders");
      },
      showSoonBadge: true,
      disabled: true,
    },
    ...getRoleMenuItem(),
    // Switch to the Manager-of-Managers (head-manager) view and back. Only for
    // users Keilot granted the MoM role — no create-fallback. The full reload
    // on the way out drops momView, restoring the single-community sidebar.
    ...(isMoM
      ? [
          {
            label: t(momView ? "exitMainManagerView" : "mainManagerView"),
            iconPath: "/icons/sidebar-icons/manage-community.svg",
            onClick: () => {
              window.location.href = momView ? "/dashboard" : "/managers-hub";
            },
          },
        ]
      : []),
    {
      label: getLanguageToggleLabel(),
      iconPath: "/icons/sidebar-icons/language.svg",
      onClick: handleLanguageToggle,
    },
    {
      label: t("logout"),
      iconPath: "/icons/sidebar-icons/logout.svg",
      isLogout: true,
      onClick: logoutHandler,
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box sx={isBelowMd ? mobileStyling : webStyling}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            {settingsMenuItems.map((item, index) => {
              const {
                label,
                iconPath,
                disabled,
                showSoonBadge,
                onClick,
                isLogout,
              } = item || {};
              return (
                <React.Fragment key={label + index}>
                  {isLogout && (
                    <Divider sx={{ borderColor: "rgba(227, 228, 237, 1)" }} />
                  )}
                  <ListItem
                    button
                    onClick={() => {
                      setIsModalVisible && setIsModalVisible(false);
                      onClick && onClick();
                    }}
                    sx={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      py: "6px",
                      px: "8px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      pointerEvents: disabled ? "none" : "auto",
                      opacity: disabled ? 0.5 : 1,
                      "&:hover": {
                        backgroundColor: "rgba(245, 246, 250, 1)",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <img
                        src={iconPath}
                        alt="logo"
                        className="menu-item-logo"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={label}
                      color="rgba(62, 63, 76, 1)"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        whiteSpace: "normal",
                        flex: "unset",
                        m: "0px",
                      }}
                    />
                    {disabled && showSoonBadge && (
                      <Box sx={soonBadge}> {t("soon")}</Box>
                    )}
                  </ListItem>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default SettingsContent;